/**
 * Source registry metadata that is safe to expose to the UI. Runtime adapter logic lives in
 * src/worker/adapters; this file only describes what each source is and what it can do.
 */
export const DISCOVERY_SOURCE_IDS = ['google_maps', 'linkedin', 'yellow_pages', 'any_website'] as const;
export type DiscoverySourceId = (typeof DISCOVERY_SOURCE_IDS)[number];

/**
 * Values stored in source_records.source.
 *
 * `csv_import` is here but not in DISCOVERY_SOURCE_IDS: it is no longer offered as a source to
 * choose, yet rows imported under it still exist and LinkedIn shares its import machinery.
 */
export const RECORD_SOURCE_IDS = [...DISCOVERY_SOURCE_IDS, 'company_website', 'csv_import'] as const;
export type RecordSourceId = (typeof RECORD_SOURCE_IDS)[number];

/**
 * There is deliberately no `linkedin_session` provider: this build has no LinkedIn crawler, so a
 * session cookie would be a credential we ask for and never send. Removing it from the enum also
 * makes the API reject an attempt to store one.
 */
export const CONNECTION_PROVIDERS = ['google_places', 'google_sheets', 'gmail', 'anthropic', 'gemini'] as const;
export type ConnectionProviderId = (typeof CONNECTION_PROVIDERS)[number];

export interface SourceCapabilities {
  discovery: boolean;
  detailPages: boolean;
  pagination: boolean;
  peopleSearch: boolean;
  jobs: boolean;
  enrichment: boolean;
}

export interface SourceDescriptor {
  id: DiscoverySourceId;
  label: string;
  tagline: string;
  description: string;
  /** How data is acquired, stated honestly. */
  acquisition: string;
  entityTypes: ReadonlyArray<'company' | 'person'>;
  capabilities: SourceCapabilities;
  /** Connection needed for live use (demo workspaces run on fixtures instead). */
  connection?: ConnectionProviderId;
  connectionOptional?: boolean;
  compliance: string;
  /** Default minimum spacing between requests to this source. */
  minIntervalMs: number;
}

export const SOURCE_DESCRIPTORS: Record<DiscoverySourceId, SourceDescriptor> = {
  google_maps: {
    id: 'google_maps',
    label: 'Google Maps',
    tagline: 'Local businesses, ratings, reviews, hours',
    description:
      'Discover local businesses by keyword and location with ratings, review counts, price level, opening hours, phone, website and coordinates.',
    acquisition:
      'Google Places API (New) Text Search with field masks and area tiling to go beyond 60 results per query. Requires a Places API key; usage is billed by Google per request.',
    entityTypes: ['company'],
    capabilities: { discovery: true, detailPages: false, pagination: true, peopleSearch: false, jobs: false, enrichment: false },
    connection: 'google_places',
    compliance:
      'Uses the official Places API under Google Maps Platform terms. Direct scraping of Google Maps pages is intentionally not implemented.',
    minIntervalMs: 120,
  },
  linkedin: {
    id: 'linkedin',
    label: 'LinkedIn',
    tagline: 'Import your LinkedIn exports (no scraping)',
    description:
      'Company size, industry, headquarters and decision-maker contacts, brought in from an export you download from LinkedIn yourself. Person rows become People, linked to the companies they work at.',
    acquisition:
      'CSV import of exports LinkedIn provides to you (Sales Navigator lists, Connections, Recruiter). This build contains no LinkedIn crawler.',
    entityTypes: ['company', 'person'],
    // Discovery is true for an import: rows become records exactly as any other source's do.
    // People search stays false — the people come from the file, they are not searched for.
    capabilities: { discovery: true, detailPages: false, pagination: false, peopleSearch: false, jobs: false, enrichment: false },
    // No credential is requested: there is nothing here that could use one, and asking for a
    // session cookie we would never send is worse than asking for nothing.
    compliance:
      "LinkedIn's robots.txt disallows every path for general crawlers (`User-agent: * / Disallow: /`) and states that automated access without permission is prohibited. This build therefore does not scrape LinkedIn — not public pages, and not with your own session. Use LinkedIn's own export, then import it here; it runs through the same cleaning, merging and enrichment as every other source.",
    minIntervalMs: 0,
  },
  yellow_pages: {
    id: 'yellow_pages',
    label: 'Yellow Pages & Directories',
    tagline: 'Business directories (configurable recipes)',
    description:
      'Business directory listings with categories, phones, websites, addresses and descriptions. Each directory is a declarative recipe, so new directories can be added without code changes.',
    acquisition:
      'Declarative directory recipes (CSS selectors, embedded JSON, JSON-LD) over static HTML or JavaScript-rendered pages, respecting robots.txt and crawl-delay.',
    entityTypes: ['company'],
    capabilities: { discovery: true, detailPages: true, pagination: true, peopleSearch: false, jobs: false, enrichment: false },
    compliance:
      "Follows each directory's robots.txt and crawl-delay. Pages protected by CAPTCHAs or bot challenges are reported as blocked and skipped.",
    minIntervalMs: 3000,
  },
  any_website: {
    id: 'any_website',
    label: 'Any Website',
    tagline: 'Point at a listing page and extract',
    description:
      'Paste a directory or listing URL, describe what you need, preview detected fields, test on a few results, then run. Detects repeated cards, JSON-LD, tables and pagination.',
    acquisition:
      'Generic page analysis (repeated structures, JSON-LD, microdata, tables, links) producing an editable extraction recipe. Optional JavaScript rendering via Browser Run.',
    entityTypes: ['company'],
    capabilities: { discovery: true, detailPages: true, pagination: true, peopleSearch: false, jobs: false, enrichment: false },
    compliance:
      'Respects robots.txt. Sites that fail the extraction test are reported as unsupported rather than producing unreliable data.',
    minIntervalMs: 2000,
  },
};

export const RECORD_SOURCE_LABELS: Record<string, string> = {
  google_maps: 'Google Maps',
  linkedin: 'LinkedIn',
  yellow_pages: 'Yellow Pages',
  any_website: 'Website listing',
  csv_import: 'CSV import',
  company_website: 'Company website',
  user: 'Manual edit',
  ai: 'AI',
  derived: 'Derived',
};

export function sourceLabel(source: string): string {
  return RECORD_SOURCE_LABELS[source] ?? source;
}

export function isDiscoverySource(value: string): value is DiscoverySourceId {
  return (DISCOVERY_SOURCE_IDS as readonly string[]).includes(value);
}
